/**
 * Collection Page Module
 *
 * Displays user's owned parcels. Uses store for data and navigation.
 * Renders thumbnails on-demand using WebAssembly.
 *
 * Card states:
 *   Saved (unclaimed): Claim + View buttons, three-dot menu (Rename, Delete)
 *   Claimed: Merch + Share + View buttons, blue tick, golden frame, three-dot menu
 */
import { deleteParcel as deleteParcelFromDB, updateParcel, uploadThumbnail, type Parcel } from "./db";
import { store, actions, selectors, type Order } from "./store";
import { getMapInstance } from "./index";
import { renderParcelThumbnailCached } from "./thumbnailRenderer";
import { openClaimModal } from "./claimModal";
import { openMerchPreview } from "./merchPreview";
import { trackEvent } from "./analytics";

// SVG icons (inline, matching app's 16x16 currentColor pattern)
const ICON_TSHIRT = `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16"><path fill="currentColor" d="M5.25 1L1 4.5 3 6.5 4.5 5v9.5h7V5l1.5 1.5L15 4.5 10.75 1A2.75 1.5 0 0 1 5.25 1Z"/></svg>`;

const ICON_SHARE = `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16"><path fill="currentColor" d="M8 1L4.5 4.5l1 1L7.25 3.75V10h1.5V3.75L10.5 5.5l1-1L8 1zM3 9v5h10V9h-1.5v3.5h-7V9H3z"/></svg>`;

const ICON_DOTS = `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16"><circle fill="currentColor" cx="8" cy="3" r="1.5"/><circle fill="currentColor" cx="8" cy="8" r="1.5"/><circle fill="currentColor" cx="8" cy="13" r="1.5"/></svg>`;

const ICON_VERIFIED = `<svg class="verified-tick" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8" fill="#1d9bf0"/><path fill="#fff" d="M6.8 11.2L3.8 8.2l1.1-1.1 1.9 1.9 4.3-4.3 1.1 1.1z"/></svg>`;

export function initCollection(): void {
  // Subscribe to parcel and order changes in store
  store.select(selectors.ownedParcels, () => {
    renderParcels();
  });

  store.select(selectors.orders, () => {
    renderParcels();
  });

  // Global click handler: close any open three-dot dropdown
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (!target.closest(".parcel-menu")) {
      document.querySelectorAll(".parcel-menu-dropdown.open").forEach((el) => {
        el.classList.remove("open");
      });
    }
  });
}

function renderParcels(): void {
  const container = document.getElementById("collectionView");
  if (!container) return;

  const parcels = store.getState().ownedParcels;

  if (parcels.length === 0) {
    container.innerHTML = `
      <h2>My Collection</h2>
      <p class="secondary">No parcels yet. Use the Save button while exploring to save locations.</p>
    `;
    return;
  }

  // Sort: claimed first (by claimed date desc), then unclaimed (by created date desc)
  const sorted = [...parcels].sort((a, b) => {
    if (a.claimed && !b.claimed) return -1;
    if (!a.claimed && b.claimed) return 1;
    if (a.claimed && b.claimed) {
      return new Date(b.claimedAt || 0).getTime() - new Date(a.claimedAt || 0).getTime();
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const orders = store.getState().orders;
  const ordersHtml = orders.length > 0 ? renderOrdersSection(orders, parcels) : "";

  container.innerHTML = `
    <h2>My Collection</h2>
    <div class="parcels-grid">
      ${sorted.map((p) => renderParcelCard(p)).join("")}
    </div>
    ${ordersHtml}
  `;

  // Render thumbnails asynchronously after DOM settles
  requestAnimationFrame(() => {
    renderThumbnails(sorted);
  });

  // Wire up event handlers
  setupActionHandlers(container, parcels);
  setupMenuHandlers(container, parcels);
  setupRenameHandlers(container, parcels);
}

/**
 * Wire up primary action buttons (View, Claim, Merch, Share)
 */
function setupActionHandlers(container: HTMLElement, parcels: Parcel[]): void {
  container.querySelectorAll("[data-view-parcel]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = (btn as HTMLElement).dataset.viewParcel;
      const parcel = parcels.find((p) => p.id === id);
      if (parcel) navigateToParcel(parcel);
    });
  });

  container.querySelectorAll("[data-claim-parcel]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = (btn as HTMLElement).dataset.claimParcel;
      const parcel = parcels.find((p) => p.id === id);
      if (parcel) openClaimModal(parcel);
    });
  });

  container.querySelectorAll("[data-merch-parcel]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = (btn as HTMLElement).dataset.merchParcel;
      const parcel = parcels.find((p) => p.id === id);
      if (parcel) openMerchPreview(parcel);
    });
  });

  container.querySelectorAll("[data-share-parcel]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = (btn as HTMLElement).dataset.shareParcel;
      const parcel = parcels.find((p) => p.id === id);
      if (parcel) openSharePopup(parcel);
    });
  });
}

/**
 * Wire up three-dot menu: toggle dropdown, Rename and Delete items
 */
function setupMenuHandlers(container: HTMLElement, parcels: Parcel[]): void {
  // Toggle dropdown
  container.querySelectorAll("[data-menu-toggle]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.menuToggle;
      const dropdown = container.querySelector(`[data-menu-dropdown="${id}"]`);
      if (!dropdown) return;

      // Close all other dropdowns first
      container.querySelectorAll(".parcel-menu-dropdown.open").forEach((el) => {
        if (el !== dropdown) el.classList.remove("open");
      });

      dropdown.classList.toggle("open");
    });
  });

  // Rename menu item
  container.querySelectorAll("[data-menu-rename]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.menuRename;
      if (id) {
        closeAllDropdowns(container);
        startEditing(id);
      }
    });
  });

  // Delete menu item
  container.querySelectorAll("[data-menu-delete]").forEach((btn) => {
    btn.addEventListener("click", async (e) => {
      e.stopPropagation();
      const id = (btn as HTMLElement).dataset.menuDelete;
      if (!id) return;

      closeAllDropdowns(container);

      const parcel = parcels.find((p) => p.id === id);
      const message = parcel?.claimed
        ? "Delete this parcel? This will also remove it from the public gallery permanently."
        : "Delete this parcel?";

      if (confirm(message)) {
        try {
          const success = await deleteParcelFromDB(id);
          if (success) {
            actions.removeParcel(id);
          } else {
            alert("Failed to delete parcel. Please try again.");
          }
        } catch (err) {
          console.error("[Collection] Delete failed:", err);
          alert("Failed to delete parcel. Please try again.");
        }
      }
    });
  });
}

function closeAllDropdowns(container: HTMLElement): void {
  container.querySelectorAll(".parcel-menu-dropdown.open").forEach((el) => {
    el.classList.remove("open");
  });
}

/**
 * Set up inline rename functionality for parcel cards
 */
function setupRenameHandlers(container: HTMLElement, parcels: Parcel[]): void {
  container.querySelectorAll("[data-rename-input]").forEach((inputEl) => {
    const input = inputEl as HTMLInputElement;
    const id = input.dataset.renameInput;
    if (!id) return;

    input.addEventListener("blur", () => {
      saveRename(id, input.value, parcels);
    });

    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        input.blur();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelEditing(id, parcels);
      }
    });
  });
}

function startEditing(parcelId: string): void {
  const nameEl = document.querySelector(`[data-parcel-name="${parcelId}"]`);
  const tickEl = nameEl?.parentElement?.querySelector(".verified-tick");
  const inputEl = document.querySelector(`[data-rename-input="${parcelId}"]`) as HTMLInputElement;

  if (nameEl && inputEl) {
    (nameEl as HTMLElement).style.display = "none";
    if (tickEl) (tickEl as HTMLElement).style.display = "none";
    inputEl.classList.remove("hidden");
    inputEl.focus();
    inputEl.select();
  }
}

function cancelEditing(parcelId: string, parcels: Parcel[]): void {
  const nameEl = document.querySelector(`[data-parcel-name="${parcelId}"]`);
  const tickEl = nameEl?.parentElement?.querySelector(".verified-tick");
  const inputEl = document.querySelector(`[data-rename-input="${parcelId}"]`) as HTMLInputElement;
  const parcel = parcels.find((p) => p.id === parcelId);

  if (nameEl && inputEl && parcel) {
    inputEl.value = parcel.name || "";
    inputEl.classList.add("hidden");
    (nameEl as HTMLElement).style.display = "";
    if (tickEl) (tickEl as HTMLElement).style.display = "";
  }
}

async function saveRename(parcelId: string, newName: string, parcels: Parcel[]): Promise<void> {
  const nameEl = document.querySelector(`[data-parcel-name="${parcelId}"]`);
  const tickEl = nameEl?.parentElement?.querySelector(".verified-tick");
  const inputEl = document.querySelector(`[data-rename-input="${parcelId}"]`) as HTMLInputElement;
  const parcel = parcels.find((p) => p.id === parcelId);

  if (!nameEl || !inputEl || !parcel) return;

  const trimmedName = newName.trim();
  const displayName = trimmedName || "Untitled";

  nameEl.textContent = displayName;
  inputEl.classList.add("hidden");
  (nameEl as HTMLElement).style.display = "";
  if (tickEl) (tickEl as HTMLElement).style.display = "";

  if (trimmedName !== (parcel.name || "")) {
    try {
      const updated = await updateParcel(parcelId, { name: trimmedName || undefined });

      if (updated) {
        actions.updateParcel(parcelId, { name: trimmedName || undefined });
      } else {
        nameEl.textContent = parcel.name || "Untitled";
        alert("Failed to rename parcel. Please try again.");
      }
    } catch (err) {
      console.error("[Collection] Rename failed:", err);
      nameEl.textContent = parcel.name || "Untitled";
      alert("Failed to rename parcel. Please try again.");
    }
  }
}

function renderParcelCard(parcel: Parcel): string {
  const date = new Date(parcel.createdAt).toLocaleDateString();
  const displayName = parcel.name || "Untitled";
  const isClaimed = parcel.claimed;

  const cardClass = isClaimed ? "parcel-card parcel-card--claimed" : "parcel-card";
  const thumbClass = isClaimed ? "parcel-thumbnail parcel-thumbnail--claimed" : "parcel-thumbnail";

  // Primary action buttons differ by state
  const actionButtons = isClaimed
    ? `
      <button data-merch-parcel="${parcel.id}" class="merch-btn" title="Create merchandise">${ICON_TSHIRT}</button>
      <button data-share-parcel="${parcel.id}" class="share-btn" title="Share">${ICON_SHARE}</button>
      <button data-view-parcel="${parcel.id}">View</button>
    `
    : `
      <button data-claim-parcel="${parcel.id}" class="claim-btn">Claim</button>
      <button data-view-parcel="${parcel.id}">View</button>
    `;

  return `
    <div class="${cardClass}" data-parcel-id="${parcel.id}">
      <div class="${thumbClass}">
        <div class="thumbnail-loading" data-thumbnail="${parcel.id}">
          <span class="thumbnail-spinner"></span>
        </div>
      </div>
      <div class="parcel-header">
        <div class="parcel-name-wrapper">
          <span class="parcel-name" data-parcel-name="${parcel.id}">${escapeHtml(displayName)}</span>
          ${isClaimed ? ICON_VERIFIED : ""}
          <input type="text" class="parcel-name-input hidden" data-rename-input="${parcel.id}" value="${escapeHtml(parcel.name || "")}" placeholder="Enter name..." maxlength="50" />
        </div>
        <div class="parcel-menu">
          <button class="parcel-menu-btn" data-menu-toggle="${parcel.id}" title="More options">${ICON_DOTS}</button>
          <div class="parcel-menu-dropdown" data-menu-dropdown="${parcel.id}">
            <button data-menu-rename="${parcel.id}">Rename</button>
            <button data-menu-delete="${parcel.id}" class="menu-delete">Delete</button>
          </div>
        </div>
      </div>
      <div class="parcel-info">
        <div class="parcel-coords">
          <span class="secondary">Re:</span> ${parcel.re.toFixed(6)}<br>
          <span class="secondary">Im:</span> ${parcel.im.toFixed(6)}
        </div>
        <div class="parcel-meta">
          <span class="secondary">Zoom:</span> ${parcel.zoom}<br>
          <span class="secondary">Date:</span> ${date}
        </div>
      </div>
      <div class="parcel-actions">
        ${actionButtons}
      </div>
    </div>
  `;
}

/**
 * Build a shareable URL for a parcel
 */
function buildParcelUrl(parcel: Parcel): string {
  const url = new URL(window.location.origin);
  url.searchParams.set("re", parcel.re.toString());
  url.searchParams.set("im", parcel.im.toString());
  url.searchParams.set("z", parcel.zoom.toString());
  url.searchParams.set("i", parcel.iterations.toString());
  url.searchParams.set("c", parcel.colorScheme);
  url.searchParams.set("r", parcel.reverseColors.toString());
  url.searchParams.set("sc", parcel.smoothColoring.toString());
  url.searchParams.set("pmin", parcel.paletteMinIter.toString());
  url.searchParams.set("pmax", parcel.paletteMaxIter.toString());
  url.searchParams.set("swi", parcel.scaleWithIterations.toString());
  url.searchParams.set("h", parcel.shiftHueAmount.toString());
  url.searchParams.set("s", parcel.saturateAmount.toString());
  url.searchParams.set("l", parcel.lightenAmount.toString());
  url.searchParams.set("cs", parcel.colorSpace.toString());
  return url.toString();
}

/**
 * Open the share popup for a claimed parcel
 */
function openSharePopup(parcel: Parcel): void {
  const modal = document.getElementById("shareModal") as HTMLDialogElement | null;
  if (!modal) return;

  // Use the dynamic share endpoint (serves OG tags for social crawlers, redirects humans)
  const shareUrl = `${window.location.origin}/api/share/${parcel.id}`;
  const directUrl = buildParcelUrl(parcel);
  const parcelName = parcel.name || "Untitled";
  const twitterText = `I discovered "${parcelName}" in the Mandelbrot set on @FractalotApp`;
  const linkedinText = `I discovered "${parcelName}" in the Mandelbrot set on #fractalot`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTwitterText = encodeURIComponent(twitterText);

  const content = modal.querySelector(".share-options");
  if (content) {
    content.innerHTML = `
      <button class="share-option" data-share-action="copy" data-share-url="${escapeHtml(directUrl)}">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16"><path fill="currentColor" d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 0 1 0 1.5h-1.5a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-1.5a.75.75 0 0 1 1.5 0v1.5A1.75 1.75 0 0 1 9.25 16h-7.5A1.75 1.75 0 0 1 0 14.25zM5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0 1 14.25 11h-7.5A1.75 1.75 0 0 1 5 9.25zm1.75-.25a.25.25 0 0 0-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 0 0 .25-.25v-7.5a.25.25 0 0 0-.25-.25z"/></svg>
        Copy URL
      </button>
      <a class="share-option" href="https://twitter.com/intent/tweet?text=${encodedTwitterText}&url=${encodedUrl}" target="_blank" rel="noopener">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16"><path fill="currentColor" d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"/></svg>
        Share on X
      </a>
      <button class="share-option" data-share-action="linkedin" data-share-text="${escapeHtml(linkedinText + "\n" + shareUrl)}" data-share-linkedin-url="https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16"><path fill="currentColor" d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854zm4.943 12.248V6.169H2.542v7.225zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016l.016-.025V6.169H6.29c.032.682 0 7.225 0 7.225z"/></svg>
        Share on LinkedIn
      </button>
      <button class="share-option" data-share-action="download" data-share-parcel-id="${parcel.id}">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16"><path fill="currentColor" d="M2.75 14A1.75 1.75 0 0 1 1 12.25v-2.5a.75.75 0 0 1 1.5 0v2.5c0 .138.112.25.25.25h10.5a.25.25 0 0 0 .25-.25v-2.5a.75.75 0 0 1 1.5 0v2.5A1.75 1.75 0 0 1 13.25 14zM7.25 7.689V2a.75.75 0 0 1 1.5 0v5.689l1.97-1.969a.749.749 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 6.78a.749.749 0 1 1 1.06-1.06z"/></svg>
        Download Image
      </button>
    `;
  }

  modal.showModal();
  trackEvent("share", { parcelId: parcel.id });

  // Wire up share action handlers (fresh each time modal opens)
  modal.querySelectorAll("[data-share-action]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const action = (btn as HTMLElement).dataset.shareAction;
      if (action === "copy") {
        const url = (btn as HTMLElement).dataset.shareUrl || "";
        try {
          await navigator.clipboard.writeText(url);
        } catch { /* clipboard unavailable */ }
        btn.textContent = "Copied!";
        setTimeout(() => {
          if (btn.parentElement) {
            // Re-render will refresh; just reset text for now
            const svg = btn.querySelector("svg");
            btn.textContent = "";
            if (svg) btn.prepend(svg);
            btn.append(" Copied!");
          }
        }, 1500);
      } else if (action === "linkedin") {
        const text = (btn as HTMLElement).dataset.shareText || "";
        const linkedinUrl = (btn as HTMLElement).dataset.shareLinkedinUrl || "";
        try {
          await navigator.clipboard.writeText(text);
        } catch { /* clipboard unavailable */ }
        const origHtml = btn.innerHTML;
        btn.innerHTML = origHtml.replace("Share on LinkedIn", "Text copied! Opening LinkedIn...");
        setTimeout(() => { window.open(linkedinUrl, "_blank", "noopener"); }, 600);
      } else if (action === "download") {
        const parcelId = (btn as HTMLElement).dataset.shareParcelId;
        const p = store.getState().ownedParcels.find((x) => x.id === parcelId);
        if (p) {
          btn.textContent = "Rendering...";
          (btn as HTMLButtonElement).disabled = true;
          try {
            const dataUrl = await renderParcelThumbnailCached(p, 1024);
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `fractalot-${p.name || p.id}.png`;
            a.click();
          } catch (err) {
            console.error("[Share] Download failed:", err);
            alert("Failed to render image. Please try again.");
          }
          (btn as HTMLButtonElement).disabled = false;
          modal.close();
        }
      }
    });
  });
}

/**
 * Initialize share modal close handlers (called once from index.ts via initCollection)
 */
function initShareModal(): void {
  const modal = document.getElementById("shareModal") as HTMLDialogElement | null;
  if (!modal) return;

  const closeBtn = document.getElementById("shareClose");
  closeBtn?.addEventListener("click", () => modal.close());

  modal.addEventListener("click", (e) => {
    if (e.target === modal) modal.close();
  });
}

// Call share modal init when collection inits
setTimeout(initShareModal, 0);

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

async function renderThumbnails(parcels: Parcel[]): Promise<void> {
  const renderPromises = parcels.map(async (parcel) => {
    const thumbnailContainer = document.querySelector(
      `[data-thumbnail="${parcel.id}"]`
    );
    if (!thumbnailContainer) return;

    try {
      const dataUrl = await renderParcelThumbnailCached(parcel, 160);

      // Guard against stale DOM references (container may have been re-rendered)
      if (!document.contains(thumbnailContainer)) return;

      thumbnailContainer.innerHTML = `<img src="${dataUrl}" alt="Parcel thumbnail" />`;
      thumbnailContainer.classList.remove("thumbnail-loading");
      thumbnailContainer.classList.add("thumbnail-loaded");

      // Background sync: upload thumbnail to Storage for claimed parcels missing one
      if (parcel.claimed && !parcel.thumbnailUrl) {
        syncThumbnailToStorage(parcel);
      }
    } catch (error) {
      console.error(`Failed to render thumbnail for ${parcel.id}:`, error);
      thumbnailContainer.innerHTML = `<span class="thumbnail-error">!</span>`;
      thumbnailContainer.classList.remove("thumbnail-loading");
      thumbnailContainer.classList.add("thumbnail-error-state");
    }
  });

  await Promise.all(renderPromises);
}

// Track parcels already queued for sync to prevent re-render loops
const syncingParcels = new Set<string>();

/**
 * Fire-and-forget: render a 512px thumbnail and upload to Supabase Storage.
 * Updates the parcel record with the public URL.
 */
async function syncThumbnailToStorage(parcel: Parcel): Promise<void> {
  if (syncingParcels.has(parcel.id)) return;
  syncingParcels.add(parcel.id);
  try {
    const dataUrl = await renderParcelThumbnailCached(parcel, 512);
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const url = await uploadThumbnail(parcel.id, blob);
    if (url) {
      actions.updateParcel(parcel.id, { thumbnailUrl: url });
      console.log(`[Collection] Synced thumbnail for ${parcel.id}`);
    }
  } catch (error) {
    console.error(`[Collection] Failed to sync thumbnail for ${parcel.id}:`, error);
  }
}

const ORDER_STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: "Pending", className: "order-status--pending" },
  paid: { label: "Paid", className: "order-status--paid" },
  confirmed: { label: "Confirmed", className: "order-status--confirmed" },
  printful_error: { label: "Error", className: "order-status--error" },
};

const PRODUCT_LABELS: Record<string, string> = {
  tshirt: "T-Shirt",
  sweatshirt: "Sweatshirt",
  mug: "Mug",
};

function renderOrdersSection(orders: Order[], parcels: Parcel[]): string {
  return `
    <h2 class="orders-heading">Orders</h2>
    <div class="orders-list">
      ${orders.map((o) => renderOrderRow(o, parcels)).join("")}
    </div>
  `;
}

function renderOrderRow(order: Order, parcels: Parcel[]): string {
  const parcel = parcels.find((p) => p.id === order.parcelId);
  const parcelName = parcel?.name || "Unknown";
  const product = PRODUCT_LABELS[order.productType] || order.productType;
  const status = ORDER_STATUS_LABELS[order.status] || { label: order.status, className: "" };
  const date = new Date(order.createdAt).toLocaleDateString();
  const details = [order.color, order.size].filter(Boolean).join(" / ");

  return `
    <div class="order-row">
      <div class="order-product">
        <span class="order-product-type">${escapeHtml(product)}</span>
        ${details ? `<span class="secondary">${escapeHtml(details)}</span>` : ""}
      </div>
      <div class="order-parcel secondary">${escapeHtml(parcelName)}</div>
      <div class="order-status ${status.className}">${status.label}</div>
      <div class="order-date secondary">${date}</div>
      ${order.errorMessage ? `<div class="order-error secondary">${escapeHtml(order.errorMessage)}</div>` : ""}
    </div>
  `;
}

function navigateToParcel(parcel: Parcel): void {
  const map = getMapInstance();
  if (!map) {
    console.error("Map not initialized");
    return;
  }

  const url = new URL(window.location.href);

  url.searchParams.set("re", parcel.re.toString());
  url.searchParams.set("im", parcel.im.toString());
  url.searchParams.set("z", parcel.zoom.toString());

  url.searchParams.set("i", parcel.iterations.toString());
  url.searchParams.set("c", parcel.colorScheme);
  url.searchParams.set("r", parcel.reverseColors.toString());
  url.searchParams.set("sc", parcel.smoothColoring.toString());
  url.searchParams.set("pmin", parcel.paletteMinIter.toString());
  url.searchParams.set("pmax", parcel.paletteMaxIter.toString());
  url.searchParams.set("swi", parcel.scaleWithIterations.toString());
  url.searchParams.set("h", parcel.shiftHueAmount.toString());
  url.searchParams.set("s", parcel.saturateAmount.toString());
  url.searchParams.set("l", parcel.lightenAmount.toString());
  url.searchParams.set("cs", parcel.colorSpace.toString());

  window.history.replaceState({}, "", url.toString());

  map.setConfigFromUrl();

  actions.setMapPosition({
    re: parcel.re,
    im: parcel.im,
    zoom: parcel.zoom,
  });

  actions.navigateTo("explorer");
}
